/**
 * Resource Icons - Centralized icon configuration for all resource types
 *
 * This file ensures consistent icons are used across the sidebar and gallery pages.
 */

import React from "react";
import {
  FaCrosshairs,
  FaChartBar,
  FaTh,
  FaBell,
  FaMagic,
  FaProjectDiagram,
  FaComments,
  FaFileAlt,
} from "react-icons/fa";
import { ResourceType } from "@/components/layout/gallery-layout/types";

export interface ResourceIconConfig {
  icon: React.ReactNode;
  iconSmall: React.ReactNode;
  iconLarge: React.ReactNode;
  label: string;
  labelPlural: string;
}

/**
 * Get the icon configuration for a resource type
 */
export function getResourceIcon(
  resourceType: ResourceType,
  size: "sm" | "md" | "lg" = "md"
): React.ReactNode {
  const config = RESOURCE_ICONS[resourceType];
  if (!config) return null;

  switch (size) {
    case "sm":
      return config.iconSmall;
    case "lg":
      return config.iconLarge;
    default:
      return config.icon;
  }
}

/**
 * Get the label for a resource type
 */
export function getResourceLabel(
  resourceType: ResourceType,
  plural: boolean = false
): string {
  const config = RESOURCE_ICONS[resourceType];
  if (!config) return resourceType;
  return plural ? config.labelPlural : config.label;
}

/**
 * Icon configurations for all resource types
 * Used by both sidebar and gallery pages for consistency
 */
export const RESOURCE_ICONS: Record<ResourceType, ResourceIconConfig> = {
  tracker: {
    icon: <FaCrosshairs size={16} />,
    iconSmall: <FaCrosshairs size={12} />,
    iconLarge: <FaCrosshairs size={20} />,
    label: "Tracker",
    labelPlural: "Trackers",
  },
  dashboard: {
    icon: <FaChartBar size={16} />,
    iconSmall: <FaChartBar size={12} />,
    iconLarge: <FaChartBar size={20} />,
    label: "Dashboard",
    labelPlural: "Dashboards",
  },
  widget: {
    icon: <FaTh size={16} />,
    iconSmall: <FaTh size={12} />,
    iconLarge: <FaTh size={20} />,
    label: "Widget",
    labelPlural: "Widgets",
  },
  alert: {
    icon: <FaBell size={16} />,
    iconSmall: <FaBell size={12} />,
    iconLarge: <FaBell size={20} />,
    label: "Alerte",
    labelPlural: "Alertes",
  },
  enrichment: {
    icon: <FaMagic size={16} />,
    iconSmall: <FaMagic size={12} />,
    iconLarge: <FaMagic size={20} />,
    label: "Enrichissement",
    labelPlural: "Enrichissements",
  },
  investigation: {
    icon: <FaProjectDiagram size={16} />,
    iconSmall: <FaProjectDiagram size={12} />,
    iconLarge: <FaProjectDiagram size={20} />,
    label: "Investigation",
    labelPlural: "Investigations",
  },
  conversation: {
    icon: <FaComments size={16} />,
    iconSmall: <FaComments size={12} />,
    iconLarge: <FaComments size={20} />,
    label: "Conversation",
    labelPlural: "Conversations",
  },
  report: {
    icon: <FaFileAlt size={16} />,
    iconSmall: <FaFileAlt size={12} />,
    iconLarge: <FaFileAlt size={20} />,
    label: "Report",
    labelPlural: "Reports",
  },
};

export default RESOURCE_ICONS;
